import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { KPICarousel, KPICard } from "../../../Components/KPICarousel";
import { getDiagnosticsAnalyticsKpis } from "../../../Api/BmsFaultsApi";
import { useBaseEntityInfo } from "../../../Hooks/usePagesContent";

const AnalyticsKPISection = ({ startDate, endDate, globalFilters = {}, vehiclesConnected }) => {
    const { baseEntityTypePluralLable } = useBaseEntityInfo();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [kpiData, setKpiData] = useState([]);

    // Helper function to calculate percentage change
    // Set lowerIsBetter=true for metrics where decrease is positive (e.g., faults, MTTR, error rates)
    const calculatePercentageChange = (current, previous, lowerIsBetter = false) => {
        if (!previous || previous === 0) return { value: 0, trend: "neutral", isPositive: true };
        const change = ((current - previous) / previous) * 100;

        let trend; // Direction: "up", "down", or "neutral"
        let isPositive; // Whether the change is good (true) or bad (false)

        if (change > 0) {
            trend = "up";
            isPositive = !lowerIsBetter; // If lower is better, increase is bad
        } else if (change < 0) {
            trend = "down";
            isPositive = lowerIsBetter; // If lower is better, decrease is good
        } else {
            trend = "neutral";
            isPositive = true;
        }

        return {
            value: Math.abs(change).toFixed(1),
            trend: trend,
            isPositive: isPositive
        };
    };

    // Helper function to convert seconds to hours
    const secondsToHours = (seconds) => {
        if (!seconds) return "0 h";
        const hours = (seconds / 3600).toFixed(1);
        return `${hours} h`;
    };

    // Helper function to calculate trend period text
    const getTrendPeriodText = (start, end) => {
        if (!start || !end) return "vs. Last 30 Days";

        const startDateObj = new Date(start);
        const endDateObj = new Date(end);
        const today = new Date();
        const todayEnd = new Date();

        // Reset time to midnight for start and today
        startDateObj.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        // Set end date to end of day for inclusive range calculation
        endDateObj.setHours(23, 59, 59, 999);

        // Check if both start and end dates are today
        todayEnd.setHours(23, 59, 59, 999);

        if (startDateObj.getTime() === today.getTime() &&
            endDateObj.getTime() <= todayEnd.getTime()) {
            return "vs. Yesterday";
        }

        // Calculate the number of days in the selected range (inclusive)
        const daysDiff = Math.round((endDateObj - startDateObj) / (1000 * 60 * 60 * 24));

        // For any other range, show "vs. last X days"
        return `vs. last ${daysDiff} day${daysDiff === 1 ? '' : 's'}`;
    };

    // Fetch KPI data from API
    useEffect(() => {
        const fetchKPIData = async () => {
            if (!startDate || !endDate) {
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const filters = {
                    startDate,
                    endDate
                };

                const response = await getDiagnosticsAnalyticsKpis(globalFilters, filters);

                // Extract data from response structure
                const kpisData = response?.response?.data;

                if (!kpisData) {
                    throw new Error("No data received from API");
                }

                // Map API response to KPI cards
                const totalFaultsChange = calculatePercentageChange(
                    kpisData.totalFaultsGenerated,
                    kpisData.totalFaultsGeneratedInPreviousTimerange,
                    true // Lower faults is better
                );

                const highFaultsChange = calculatePercentageChange(
                    kpisData.totalHighFaultsGenerated,
                    kpisData.totalHighFaultsGeneratedInPreviousTimerange,
                    true // Lower faults is better
                );

                const resolveFaultsChange = calculatePercentageChange(
                    kpisData.totalFaultsResolved,
                    kpisData.totalFaultsResolvedInPreviousTimerange,
                    false //higher resolution is better 
                );

                const criticalFaultsResolvedChange = calculatePercentageChange(
                    kpisData.totalCriticalFaultsResolved,
                    kpisData.totalCriticalFaultsResolvedInPreviousTimerange,
                    false //higher resolution is better 
                );

                const highFaultsResolvedChange = calculatePercentageChange(
                    kpisData.totalHighFaultsResolved,
                    kpisData.totalHighFaultsResolvedInPreviousTimerange,
                    false //higher resolution is better 
                );

                const criticalFaultsChange = calculatePercentageChange(
                    kpisData.totalCriticalFaultsGenerated,
                    kpisData.totalCriticalFaultsGeneratedInPreviousTimerange,
                    true // Lower faults is better
                );

                // const escalationsChange = calculatePercentageChange(
                //     kpisData.totalFaultsEscalated,
                //     kpisData.totalFaultsEscalatedInPreviousTimerange,
                //     true // Lower escalations is better
                // );

                const mttrChange = calculatePercentageChange(
                    kpisData.meanTimeToResolveInSecs,
                    kpisData.meanTimeToResolveInSecsInPreviousTimerange,
                    true // Lower mttr is better
                );

                const trendPeriod = getTrendPeriodText(startDate, endDate);

                const mappedData = [
                    {
                        title: "Total Faults Triggered",
                        titleTooltip: `Total number of faults triggered by the system across all ${baseEntityTypePluralLable.toLowerCase()} during the selected period.`,
                        value: kpisData.totalFaultsGenerated?.toString() || "0",
                        previousValue: kpisData.totalFaultsGeneratedInPreviousTimerange?.toString() || "0",
                        trend: totalFaultsChange.trend,
                        isPositive: totalFaultsChange.isPositive,
                        trendValue: `${totalFaultsChange.value}%`,
                        trendPeriod: trendPeriod,
                        metrics: [
                            {
                                label: `${baseEntityTypePluralLable} Affected`,
                                value: kpisData.totalBatteriesAffected?.toString() || "0"
                            },
                            {
                                label: "Avg. Triggers Per Day",
                                value: kpisData.averageFaultsGeneratedPerDay?.toString() || "0"
                            },
                        ],
                    },
                    // {
                    //     title: "Critical Faults Triggered",
                    //     titleTooltip: "Total number of alerts marked as Critical — indicating urgent issues that need immediate resolution.",
                    //     value: kpisData.totalCriticalFaultsGenerated?.toString() || "0",
                    //     previousValue: kpisData.totalCriticalFaultsGeneratedInPreviousTimerange?.toString() || "0",
                    //     trend: criticalFaultsChange.trend,
                    //     isPositive: criticalFaultsChange.isPositive,
                    //     trendValue: `${criticalFaultsChange.value}%`,
                    //     trendPeriod: trendPeriod,
                    //     metrics: [
                    //         {
                    //             label: `${baseEntityTypePluralLable} Affected`,
                    //             value: kpisData.totalCriticalFaultsBatteriesAffected?.toString() || "0"
                    //         },
                    //         {
                    //             label: "Avg. Triggers Per Day",
                    //             value: kpisData.averageCriticalFaultsGeneratedPerDay?.toString() || "0"
                    //         },
                    //     ],
                    // },
                    {
                        title: "High Severity Faults Triggered",
                        titleTooltip: "Total number of faults marked as High severity — indicating urgent issues that need immediate attention.",
                        value: kpisData.totalHighFaultsGenerated?.toString() || "0",
                        previousValue: kpisData.totalHighFaultsGeneratedInPreviousTimerange?.toString() || "0",
                        trend: highFaultsChange.trend,
                        isPositive: highFaultsChange.isPositive,
                        trendValue: `${highFaultsChange.value}%`,
                        trendPeriod: trendPeriod,
                        metrics: [
                            {
                                label: `${baseEntityTypePluralLable} Affected`,
                                value: kpisData.totalHighFaultsBatteriesAffected?.toString() || "0"
                            },
                            {
                                label: "Avg. Triggers Per Day",
                                value: kpisData.averageHighFaultsGeneratedPerDay?.toString() || "0"
                            },
                        ],
                    },
                    {
                        title: "Total Faults Resolved",
                        titleTooltip: "Total faults resolved for all severity — indicating total faults resolved for selected time period",
                        value: kpisData.totalFaultsResolved?.toString() || "0",
                        previousValue: kpisData.totalFaultsResolvedInPreviousTimerange?.toString() || "0",
                        trend: resolveFaultsChange.trend,
                        isPositive: resolveFaultsChange.isPositive,
                        trendValue: `${resolveFaultsChange.value}%`,
                        trendPeriod: trendPeriod,
                        metrics: [
                            {
                                label: `${baseEntityTypePluralLable} Affected`,
                                value: kpisData.totalFaultsResolvedBatteriesAffected?.toString() || "0"
                            },
                            {
                                label: "Avg. Resolved Per Day",
                                value: kpisData.averageFaultsResolvedPerDay?.toString() || "0"
                            },
                        ],
                    },
                    // {
                    //     title: "Critical Faults Resolved",
                    //     titleTooltip: "Faults resolved for critical severity — indicating critical faults resolved for selected time period",
                    //     value: kpisData.totalCriticalFaultsResolved?.toString() || "0",
                    //     previousValue: kpisData.totalCriticalFaultsResolvedInPreviousTimerange?.toString() || "0",
                    //     trend: criticalFaultsResolvedChange.trend,
                    //     isPositive: criticalFaultsResolvedChange.isPositive,
                    //     trendValue: `${criticalFaultsResolvedChange.value}%`,
                    //     trendPeriod: trendPeriod,
                    //     metrics: [
                    //         {
                    //             label: `${baseEntityTypePluralLable} Affected`,
                    //             value: kpisData.totalCriticalFaultsResolvedBatteriesAffected?.toString() || "0"
                    //         },
                    //         {
                    //             label: "Avg. Resolved Per Day",
                    //             value: kpisData.averageCriticalFaultsResolvedPerDay?.toString() || "0"
                    //         },
                    //     ],
                    // },
                    {
                        title: "High Severity Faults Resolved",
                        titleTooltip: "Faults resolved for high severity — indicating high severity faults resolved for selected time period",
                        value: kpisData.totalHighFaultsResolved?.toString() || "0",
                        previousValue: kpisData.totalHighFaultsResolvedInPreviousTimerange?.toString() || "0",
                        trend: highFaultsResolvedChange.trend,
                        isPositive: highFaultsResolvedChange.isPositive,
                        trendValue: `${highFaultsResolvedChange.value}%`,
                        trendPeriod: trendPeriod,
                        metrics: [
                            {
                                label: `${baseEntityTypePluralLable} Affected`,
                                value: kpisData.totalHighFaultsResolvedBatteriesAffected?.toString() || "0"
                            },
                            {
                                label: "Avg. Resolved Per Day",
                                value: kpisData.averageHighFaultsResolvedPerDay?.toString() || "0"
                            },
                        ],
                    },
                    // {
                    //     title: "Escalations",
                    //     titleTooltip: "Total number of faults where severity increased (e.g., from Warning to High) during the fault lifecycle.",
                    //     value: kpisData.totalFaultsEscalated?.toString() || "0",
                    //     previousValue: kpisData.totalFaultsEscalatedInPreviousTimerange?.toString() || "0",
                    //     trend: escalationsChange.trend,
                    //     isPositive: escalationsChange.isPositive,
                    //     trendValue: `${escalationsChange.value}%`,
                    //     trendPeriod: trendPeriod,
                    //     metrics: [
                    //         {
                    //             label: "Vehicles Affected",
                    //             value: kpisData.totalFaultsEscalatedBatteriesAffected?.toString() || "0"
                    //         },
                    //         {
                    //             label: "Warning → High",
                    //             value: kpisData.totalFaultsEscalated?.toString() || "0"
                    //         },
                    //     ],
                    // },
                    {
                        title: "Mean Time to Resolve",
                        titleTooltip: "Average time taken to resolve faults from the moment they were raised to when they were marked as resolved — helps assess operational responsiveness",
                        value: secondsToHours(kpisData.meanTimeToResolveInSecs),
                        previousValue: secondsToHours(kpisData.meanTimeToResolveInSecsInPreviousTimerange),
                        trend: mttrChange.trend,
                        isPositive: mttrChange.isPositive,
                        trendValue: `${mttrChange.value}%`,
                        trendPeriod: trendPeriod,
                        metrics: [
                            {
                                label: "Resolution Rate",
                                value: `${kpisData.resolutionRatePercent?.toString()}%`  || "0"
                            },
                            {
                                label: "MTTR of High Severity Faults",
                                value: secondsToHours(kpisData.highMeanTimeToResolve)
                            },
                        ],
                    },
                ];

                setKpiData(mappedData);
            } catch (err) {
                console.error("Error fetching KPI data:", err);
                setError(err?.message || "Failed to load KPI data");
            } finally {
                setLoading(false);
            }
        };

        fetchKPIData();
    }, [startDate, endDate, globalFilters, baseEntityTypePluralLable]);

    const renderKPICard = (kpi) => (
        <KPICard
            title={kpi.title}
            titleTooltip={kpi.titleTooltip}
            value={kpi.value}
            unit={kpi.unit}
            previousValue={kpi.previousValue}
            trend={kpi.trend}
            isPositive={kpi.isPositive}
            trendValue={kpi.trendValue}
            trendPeriod={kpi.trendPeriod}
            metrics={kpi.metrics}
            totalVehicles={vehiclesConnected}
        />
    );

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            {/* KPI Section Header */}
            <Box>
                <Typography
                    variant="fw400s20c000"
                    sx={{
                        lineHeight: "28px",
                        fontWeight: 300,
                    }}
                >
                    Key Performance Indicators
                </Typography>
            </Box>

            {/* KPI Carousel */}
            <KPICarousel
                data={kpiData}
                renderCard={renderKPICard}
                loading={loading}
                error={error}
            />
        </Box>
    );
};

export default AnalyticsKPISection;
